import React, { useState } from 'react';
import Papa from 'papaparse';
import { api } from '../api';
import type { PlantInput } from '../types';
import { CATEGORIES } from '../constants';

interface CsvImportProps {
  onComplete: () => void;
  onClose: () => void;
}

export const CsvImport: React.FC<CsvImportProps> = ({ onComplete, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>(CATEGORIES[0]);
  const [preview, setPreview] = useState<PlantInput[] | null>(null);
  const [progress, setProgress] = useState<string>('');

  const processCsv = (text: string) => {
    Papa.parse<any>(text, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          setError('Fehler beim Parsen der CSV-Datei: ' + results.errors[0].message);
          return;
        }

        const items: PlantInput[] = [];
        let skipped = 0;

        for (const row of results.data) {
          // Check for keys that match the columns loosely
          const botKey = Object.keys(row).find((k) => {
            const kl = k.toLowerCase();
            return kl.includes('botanisch') || kl.includes('verwendung');
          });
          const gerKey = Object.keys(row).find((k) => {
            const kl = k.toLowerCase();
            return kl.includes('deutsch') || kl === 'name' || (kl.includes('name') && !kl.includes('botanisch'));
          });
          const nrKey = Object.keys(row).find((k) => k.toLowerCase().includes('nr'));
          const fapKey = Object.keys(row).find((k) => k.toLowerCase().includes('kernsortiment') || k.toLowerCase().includes('fap'));

          const botanical = botKey ? row[botKey]?.trim() : '';
          const german = gerKey ? row[gerKey]?.trim() : '';
          const nr = nrKey ? row[nrKey]?.trim() : '';
          const fap = fapKey ? row[fapKey]?.trim() : '';

          if (!botanical || !german) {
            skipped++;
            continue;
          }

          const notesArr = [];
          if (nr) notesArr.push(`Nr: ${nr}`);
          if (fap) notesArr.push(`Kernsortiment: ${fap}`);

          items.push({
            botanical_name: botanical,
            german_name: german,
            notes: notesArr.length > 0 ? notesArr.join(' | ') : null,
            image_url: null,
            category: category,
          });
        }

        if (items.length === 0) {
          setError(`Keine gültigen Daten gefunden. (Erwartet werden Spalten wie "Deutsche Bezeichnung"/"Name" und "Botanische Bezeichnung"/"Verwendung"). ${skipped > 0 ? `(${skipped} Zeilen übersprungen)` : ''}`);
        } else {
          setPreview(items);
          setError(null);
        }
      },
      error: (err: any) => setError('Kritischer Fehler: ' + err.message)
    });
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setLoading(true);
    const reader = new FileReader();
    reader.onload = (evt) => {
      setLoading(false);
      const text = evt.target?.result as string;
      if (text) processCsv(text);
    };
    reader.onerror = () => {
      setLoading(false);
      setError('Fehler beim Lesen der Datei');
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setLoading(true);
      const reader = new FileReader();
      reader.onload = (evt) => {
        setLoading(false);
        const text = evt.target?.result as string;
        if (text) processCsv(text);
      };
      reader.onerror = () => {
        setLoading(false);
        setError('Fehler beim Lesen der Datei');
      };
      reader.readAsText(file);
    }
  };

  const importData = async () => {
    if (!preview || preview.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      // Re-apply the selected category in case it was changed after parsing and truncate Verwendung if needed
      const finalItems = preview.map((p) => {
        const botanical_name = p.botanical_name;
        return {
          ...p,
          botanical_name,
          category,
        };
      });
      setProgress(`Importiere ${finalItems.length} Pflanzen...`);
      await api.createPlantsBulk(finalItems);
      setProgress('Abgeschlossen!');
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-cream/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative panel w-full max-w-2xl max-h-[90vh] flex flex-col animate-fadeIn">
        
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center border border-sage/20 text-sage">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
            </div>
            <div>
              <h2 className="plant-name text-xl font-semibold text-cream">CSV Import</h2>
              <p className="text-creamDim text-xs">Pflanzenlisten auf einen Schlag importieren</p>
            </div>
          </div>
          <button onClick={onClose} className="btn-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 bg-red-50 text-red-600 p-3 rounded-lg border border-red-100 text-sm flex gap-2 items-start">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 flex-shrink-0">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          {!preview ? (
            <div className="flex flex-col gap-5">
              <div 
                className="border-2 border-dashed border-border rounded-xl p-10 flex flex-col items-center justify-center gap-3 bg-paper/50 hover:bg-paper hover:border-sage/50 transition-colors cursor-pointer"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => document.getElementById('csv-upload')?.click()}
              >
                <input type="file" id="csv-upload" accept=".csv" className="hidden" onChange={handleFile} />
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-sage/60">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <div className="text-center">
                  <p className="text-sm font-medium text-sage">CSV-Datei hierher ziehen oder klicken</p>
                  <p className="text-xs text-creamDim mt-1">
                    Pflanzen: "Deutsche Bezeichnung" & "Botanische Bezeichnung"
                  </p>
                </div>
              </div>

              <div className="text-xs text-creamDim/80 bg-goldLight/20 p-4 rounded-xl border border-gold/20 leading-relaxed">
                <strong className="text-gold font-medium block mb-1">Tipp:</strong>
                Die Datei sollte Komma- oder Semikolon-getrennt sein.
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="bg-sage/10 p-4 rounded-xl border border-sage/20">
                <p className="text-sage font-medium flex items-center gap-2 mb-3">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  {preview.length} Pflanzen erfolgreich geparst
                </p>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-creamDim font-medium">Kategorie für alle importierten Pflanzen:</label>
                  <select 
                    className="input w-full bg-white"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="border border-border/60 rounded-xl overflow-hidden max-h-60 overflow-y-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-paper sticky top-0 border-b border-border/60 text-xs text-creamDim/70">
                    <tr>
                      <th className="px-4 py-2 font-medium">Deutsch</th>
                      <th className="px-4 py-2 font-medium">Botanisch</th>
                      <th className="px-4 py-2 font-medium">Notizen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {preview.slice(0, 10).map((p, i) => (
                      <tr key={i} className="hover:bg-paper/50 transition-colors">
                        <td className="px-4 py-2 text-cream font-medium">{p.german_name}</td>
                        <td className="px-4 py-2 text-sage italic">{p.botanical_name}</td>
                        <td className="px-4 py-2 text-creamDim/70 text-xs truncate max-w-[150px]">{p.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.length > 10 && (
                  <div className="bg-paper/80 p-2 text-center text-xs text-creamDim border-t border-border/40">
                    Und {preview.length - 10} weitere...
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-border/50 flex justify-end gap-3 bg-paper/30">
          <button onClick={onClose} className="btn-ghost" disabled={loading}>
            Abbrechen
          </button>
          
          {preview && (
            <button onClick={importData} className="btn-primary" disabled={loading}>
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> {progress || 'Importiere...'}</>
              ) : (
                <>Jetzt importieren</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
